/**
 * Topic Card · v4
 * - 已 IP 化卡片:每个指标用 Radix Tooltip 完整说明(不截断)
 * - 未 IP 化的卡片不出现在主榜(由 RadarPage 过滤),保留对 banned 显示
 */
import { Flame, Sparkles, Zap, AlertTriangle, EyeOff, BookOpen, Users, Wand2 } from 'lucide-react';
import type { TopicCard } from '@starfox/shared-types';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';

interface Props {
  topic: TopicCard;
  onOpenDetail?: (t: TopicCard) => void;
  onKickoff?: (t: TopicCard) => void;
  onIpize?: (t: TopicCard) => void;
  ipizing?: boolean;
}

const TIPS = {
  heat: '跨源热度(0-100):由抖音 / 快手 / B 站 / 微博 / 小红书等多平台热搜分加权得到。100 表示当前在某榜首位;低于 30 通常说明只有单一平台关注。',
  riskRed: '政策红线:涉政、外交、民族、宗教、领导人、军警、灾难等高风险领域。建议直接屏蔽不立项,以避免合规风险。',
  riskYellow: '政策黄线:可能擦边但仍可改编。需要在剧本阶段重点规避具体词、避免敏感细节出现在台词或镜头中。',
  suit: '改编适配度(0-100):LLM 评估「热搜事件 → 短剧剧情」的可改编程度。新闻 / 体育赛事 / 财报通常 < 30;有人物冲突 50-70;直接套甜宠 / 战神 / 重生模板 ≥ 85。',
  hook: '钩子密度(每分钟反转 / 悬念点估值,范围 0-2):短剧黄金线 ≥ 1.2,即每分钟至少一次反转或悬念释放。低于 0.8 容易导致观众弃剧。',
  cpm: 'CPM(千次曝光预估收入,单位元):综合题材热度 + 受众商业价值估算。已 IP 化卡片优先使用 IP 包内估值,未 IP 化使用题材均值。',
  cheers: '爽点关键词:LLM 从 IP 设计中提炼的核心反转 / 打脸 / 逆袭点(如「退婚反杀」「身份揭秘」「打脸前男友」),决定观众情绪释放节奏。',
  ipName: 'IP 名称:LLM 二次创作的短剧立项名(≤15 字),不照搬原热搜标题,聚焦冲突主题方便后续投流和搜索。',
  logline: '一句话剧情钩(40-50 字):体现身份反转、打脸、逆袭等主线,作为剧本立项与投流脚本的核心 elevator pitch。',
  origin: '原始热搜标题:来自抓取源(抖音 / B 站 / 微博等),仅作背景参考,实际立项以上方 IP 名 + Logline 为准。',
  genres: '题材标签:来自 25 类官方分类法,允许多选叠加(主题材为第一项,其余作为附加标签)。',
  audiences: '目标人群:来自 15 类核心受众。多选意味着这条 IP 在多个圈层都有破圈潜力。',
};

export function RadarCard({ topic, onOpenDetail, onKickoff, onIpize, ipizing }: Props) {
  const d = topic.dimensions;
  const seed = topic.ipSeed ?? null;
  const banned = !!topic.bannedReason;
  const suit = topic.dramaSuitability ?? 0;
  const risk = topic.policyRisk ?? 'none';
  const genres = (topic.genres ?? []).slice(0, 3);
  const auds = (topic.audiences ?? []).slice(0, 3);
  const hasIp = !!seed?.ipName;

  return (
    <Card
      className={cn(
        'group flex h-full flex-col overflow-hidden transition hover:shadow-m2',
        banned && 'border-danger/30 bg-danger-soft/30',
        !hasIp && !banned && 'border-dashed border-outline-variant/70',
      )}
    >
      {/* Header */}
      <CardHeader className="space-y-2 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {hasIp ? (
              <>
                <InfoTooltip content={TIPS.ipName}>
                  <span className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-brand-500">
                    <BookOpen className="h-3 w-3" />
                    <span className="truncate font-semibold">{seed!.ipName}</span>
                  </span>
                </InfoTooltip>
                {seed!.loglinePinned && (
                  <h3 className="text-sm font-semibold leading-snug text-neutral-800">
                    {seed!.loglinePinned}
                  </h3>
                )}
                <InfoTooltip
                  content={
                    <>
                      <div className="font-medium text-white">原始热搜标题</div>
                      <div className="mt-1 text-neutral-200">{topic.title}</div>
                      <div className="mt-2 text-[10px] text-neutral-400">{TIPS.origin}</div>
                    </>
                  }
                >
                  <p className="mt-1 line-clamp-1 text-[11px] text-neutral-400">来源:{topic.title}</p>
                </InfoTooltip>
              </>
            ) : (
              <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-neutral-800">
                {topic.title}
              </h3>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <InfoTooltip content={TIPS.heat}>
              <Badge variant="warning">
                <Flame className="h-3 w-3" />
                {(d.heat * 100).toFixed(0)}
              </Badge>
            </InfoTooltip>
            {risk === 'red' && (
              <InfoTooltip
                content={
                  <>
                    <div className="font-medium text-white">政策红线</div>
                    <div className="mt-1">{TIPS.riskRed}</div>
                    {topic.policyReason && (
                      <div className="mt-2 text-neutral-300">原因:{topic.policyReason}</div>
                    )}
                  </>
                }
              >
                <Badge variant="danger">红</Badge>
              </InfoTooltip>
            )}
            {risk === 'yellow' && (
              <InfoTooltip
                content={
                  <>
                    <div className="font-medium text-white">政策黄线</div>
                    <div className="mt-1">{TIPS.riskYellow}</div>
                    {topic.policyReason && (
                      <div className="mt-2 text-neutral-300">原因:{topic.policyReason}</div>
                    )}
                  </>
                }
              >
                <Badge variant="warning">黄</Badge>
              </InfoTooltip>
            )}
          </div>
        </div>

        {!hasIp && d.conflictSkeleton && (
          <p className="line-clamp-2 text-xs leading-relaxed text-neutral-600">
            {d.conflictSkeleton}
          </p>
        )}
      </CardHeader>

      <CardContent className="flex-1 space-y-2 pt-1">
        {genres.length > 0 && (
          <div className="flex items-start gap-1.5">
            <InfoTooltip content={TIPS.genres}>
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-outline" />
            </InfoTooltip>
            <div className="flex flex-wrap items-center gap-1">
              {genres.map((g) => (
                <Badge key={g} variant="info" className="text-[10px]">
                  {g}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {auds.length > 0 && (
          <div className="flex items-start gap-1.5">
            <InfoTooltip content={TIPS.audiences}>
              <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-outline" />
            </InfoTooltip>
            <div className="flex flex-wrap items-center gap-1">
              {auds.map((a) => (
                <Badge key={a} variant="success" className="text-[10px]">
                  {a}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-1 pt-1 text-[10px] text-neutral-500">
          <Metric
            label="适配"
            value={`${(suit * 100).toFixed(0)}`}
            highlight={suit >= 0.7}
            tip={TIPS.suit}
          />
          <Metric label="钩子" value={`${d.hookDensity.toFixed(1)}/m`} tip={TIPS.hook} />
          <Metric
            label="CPM"
            value={`¥${(seed?.estimatedCpm ?? d.commercial.cpmEst).toFixed(0)}`}
            tip={TIPS.cpm}
          />
        </div>

        {seed?.cheerPoints && seed.cheerPoints.length > 0 && (
          <div className="flex items-center gap-1.5 pt-1">
            <InfoTooltip content={TIPS.cheers}>
              <Zap className="h-3.5 w-3.5 text-warning" />
            </InfoTooltip>
            <div className="flex flex-wrap items-center gap-1">
              {seed.cheerPoints.slice(0, 4).map((c, i) => (
                <span
                  key={`${c}-${i}`}
                  className="rounded bg-warning-soft px-1.5 py-0.5 text-[10px] text-warning"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {banned && (
          <div className="flex items-start gap-1 rounded-md bg-danger-soft p-1.5 text-[10px] text-danger">
            <EyeOff className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{topic.bannedReason}</span>
          </div>
        )}

        {!banned && risk !== 'none' && topic.policyReason && (
          <div className="flex items-start gap-1 text-[10px] text-warning">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{topic.policyReason}</span>
          </div>
        )}
      </CardContent>

      <CardFooter className="gap-2 pt-2">
        {banned ? (
          <Button variant="ghost" size="sm" className="flex-1" onClick={() => onOpenDetail?.(topic)}>
            查看详情
          </Button>
        ) : !hasIp ? (
          <>
            <Button
              variant="agent"
              agentColor="radar"
              size="sm"
              className="flex-1"
              onClick={() => onIpize?.(topic)}
              disabled={ipizing}
              title="重新调用 LLM 生成完整 IP 包(题材/人群/Logline/爽点)"
            >
              <Wand2 className={cn('h-3.5 w-3.5', ipizing && 'animate-pulse')} />
              {ipizing ? '生成中…' : 'IP 化'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onOpenDetail?.(topic)}>
              详情
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="agent"
              agentColor="radar"
              size="sm"
              className="flex-1"
              onClick={() => onKickoff?.(topic)}
              disabled={ipizing}
              title={ipizing ? 'IP 重新生成中,完成后再立项' : undefined}
            >
              {ipizing ? (
                <>
                  <Wand2 className="h-3.5 w-3.5 animate-pulse" />
                  IP 生成中…
                </>
              ) : (
                '一键立项'
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onOpenDetail?.(topic)}>
              详情
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}

function Metric({
  label,
  value,
  highlight,
  tip,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  tip?: string;
}) {
  const inner = (
    <div className="flex w-full flex-col items-center rounded bg-surface-variant px-1 py-1">
      <span className="text-outline">{label}</span>
      <span
        className={cn(
          'mt-0.5 font-medium tabular-nums',
          highlight ? 'text-brand-500' : 'text-neutral-700',
        )}
      >
        {value}
      </span>
    </div>
  );
  if (!tip) return inner;
  return (
    <InfoTooltip content={tip} className="w-full">
      {inner}
    </InfoTooltip>
  );
}
